import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, type ReactNode } from "react";

import { authApi, type LoginInput, type RegisterInput } from "@/api/auth";
import { onUnauthenticated } from "@/api/client";
import { ApiError } from "@/api/errors";
import { queryKeys } from "@/api/queryKeys";
import type { MeResponse } from "@/api/types";

import { AuthContext, type AuthContextValue, type AuthStatus } from "./context";

/**
 * Sessão do usuário.
 *
 * A verdade sobre "estou logado?" é SEMPRE o backend: perguntamos em
 * `/api/v1/auth/me`. Não existe token no localStorage, não há estado de
 * autenticação inventado no cliente.
 *
 * Qualquer 401 vindo de qualquer endpoint derruba a sessão local
 * imediatamente (ver `onUnauthenticated` no cliente HTTP).
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const meQuery = useQuery<MeResponse | null>({
    queryKey: queryKeys.auth.me(),
    queryFn: async ({ signal }) => {
      try {
        return await authApi.me(signal);
      } catch (error) {
        // 401 aqui é resposta esperada: significa "não autenticado".
        if (error instanceof ApiError && error.isUnauthenticated) return null;
        throw error;
      }
    },
    retry: false,
    staleTime: 60_000,
  });

  // Um 401 em QUALQUER requisição invalida a sessão local na hora.
  useEffect(() => {
    return onUnauthenticated(() => {
      // Zera o usuário corrente...
      queryClient.setQueryData(queryKeys.auth.me(), null);

      // ...e descarta os dados privados em cache. A própria query `auth` é
      // PRESERVADA: removê-la aqui apagaria o `null` recém-escrito e
      // dispararia um refetch que levaria a outro 401, em laço.
      queryClient.removeQueries({
        predicate: (query) => query.queryKey[0] !== "auth",
      });
    });
  }, [queryClient]);

  const loginMutation = useMutation({
    mutationFn: (input: LoginInput) => authApi.login(input),
    onSuccess: async () => {
      await queryClient.invalidateQueries();
    },
  });

  const registerMutation = useMutation({
    mutationFn: (input: RegisterInput) => authApi.register(input),
    onSuccess: async () => {
      await queryClient.invalidateQueries();
    },
  });

  const logoutMutation = useMutation({
    mutationFn: () => authApi.logout(),
    onSettled: () => {
      queryClient.setQueryData(queryKeys.auth.me(), null);
      queryClient.clear();
    },
  });

  const login = useCallback<AuthContextValue["login"]>(
    async ({ email, password, accountId }) => {
      await loginMutation.mutateAsync({
        email,
        password,
        ...(accountId === undefined ? {} : { account_id: accountId }),
      });
    },
    [loginMutation],
  );

  const register = useCallback<AuthContextValue["register"]>(
    async ({ accountName, name, email, password, document }) => {
      await registerMutation.mutateAsync({
        // Só vai para a API quando o usuário realmente informou um nome.
        ...(accountName ? { account_name: accountName } : {}),
        name,
        email,
        password,
        ...(document === undefined || document === "" ? {} : { document }),
      });
    },
    [registerMutation],
  );

  const logout = useCallback<AuthContextValue["logout"]>(async () => {
    await logoutMutation.mutateAsync();
  }, [logoutMutation]);

  const refresh = useCallback<AuthContextValue["refresh"]>(async () => {
    await meQuery.refetch();
  }, [meQuery]);

  const value = useMemo<AuthContextValue>(() => {
    const me = meQuery.data ?? null;

    let status: AuthStatus = "loading";
    if (meQuery.isPending) status = "loading";
    else if (me?.user) status = "authenticated";
    else status = "unauthenticated";

    const account = me?.user.account ?? null;
    const role = me?.user.role ?? null;

    return {
      status,
      user: me?.user ?? null,
      account,
      accounts: me?.accounts ?? [],
      role,
      canManageAccount: role === "owner" || role === "admin",
      login,
      register,
      logout,
      refresh,
    };
  }, [meQuery.data, meQuery.isPending, login, register, logout, refresh]);

  return <AuthContext value={value}>{children}</AuthContext>;
}
